import { isDrawableByPdfMake } from "../pdf-image.js";
import type { TDocumentDefinitions, Content, TableCell } from "pdfmake/interfaces.js";
import type { DocumentBuilder, DocumentSnapshot } from "../document-engine.js";
import { documentEngine } from "../document-engine.js";
import { INSPECTION_REPORT_HEADER_STYLES, buildInspectionReportHeader } from "./inspection-report-header.js";
import { isPdfDrawableImage } from "./image-size.js";

/** A4 width (595.28pt) minus the 40pt side margins — nothing may exceed this. */
const USABLE_WIDTH = 515;

/** Layout with horizontal rules only: keeps the document readable without the grid look. */
const CLEAN_LAYOUT = {
  hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 0.6 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => "#D6D6D6",
  paddingTop: () => 4,
  paddingBottom: () => 4,
  paddingLeft: () => 6,
  paddingRight: () => 6
};

/**
 * Results table: tighter padding so the seven columns fit the page.
 * pdfmake treats numeric widths as CONTENT width and adds the cell padding on
 * top, so the horizontal padding has to be budgeted for (see RESULTS_WIDTHS).
 */
const RESULTS_CELL_PADDING = 3;

const RESULTS_LAYOUT = {
  hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 0.6 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => "#D6D6D6",
  paddingTop: () => 3,
  paddingBottom: () => 3,
  paddingLeft: () => RESULTS_CELL_PADDING,
  paddingRight: () => RESULTS_CELL_PADDING
};

/**
 * Content widths of the results table. Their sum plus the padding of all
 * seven columns must never exceed USABLE_WIDTH:
 *   473 + (7 × 2 × 3) = 473 + 42 = 515pt exactly.
 */
const RESULTS_WIDTHS = [60, 105, 105, 46, 46, 49, 62];

/**
 * Mesma tabela em MEDIÇÃO EM ALTA TEMPERATURA — MÉTODO A (aproximado):
 *   PONTO | COMPONENTE | LOCALIZAÇÃO | NOM | MÍN | TME | Emq | FC_A | Ea | RESULTADO
 * São dez colunas (o Método A não tem coluna de calibração), então o
 * orçamento horizontal é refeito do zero:
 *   435 + (10 × 2 × 3) = 435 + 60 = 495pt, dentro dos 515 utilizáveis.
 * Componente e Localização cedem a largura — são os únicos campos que
 * quebram em duas linhas sem prejuízo de leitura.
 */
const RESULTS_WIDTHS_HT = [42, 62, 62, 32, 32, 30, 36, 38, 36, 65];

/** Same, plus outer vertical rules — used where a boxed area reads better (croqui, observações). */
const BOX_LAYOUT = {
  hLineWidth: () => 0.6,
  vLineWidth: () => 0.6,
  hLineColor: () => "#D6D6D6",
  vLineColor: () => "#D6D6D6",
  paddingTop: () => 6,
  paddingBottom: () => 6,
  paddingLeft: () => 8,
  paddingRight: () => 8
};

/**
 * Builds the official "Relatório de Inspeção de Medição de Espessura (ME)".
 *
 * Pagination is fully dynamic (no forced pageBreak): pdfmake flows the
 * content and only the native header/footer repeat. Every field is optional-
 * safe — a missing croqui, comment or signature must never break generation.
 */
export class MeBuilder implements DocumentBuilder {
  build(snapshot: DocumentSnapshot): TDocumentDefinitions {
    const data = snapshot.data || {};

    return {
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [40, 118, 40, 46],
      header: () => this.buildHeader(snapshot, data),
      footer: (currentPage: number, pageCount: number) => ({
        margin: [40, 8, 40, 0],
        columns: [
          { text: "INSPECT IA | Relatório de Medição de Espessura | FORM-ME | REV.00", style: "footerText" },
          { text: `Página ${currentPage} de ${pageCount}`, style: "footerText", alignment: "right" }
        ]
      }),
      content: [
        this.buildSection("01", "IDENTIFICAÇÃO", this.buildIdentification(data)),
        this.buildSection("02", "PARÂMETROS DO ENSAIO", this.buildParameters(data)),
        this.buildSection("03", "APARELHO E TRANSDUTOR", this.buildEquipment(data)),
        this.buildSection("04", "RESULTADOS DAS MEDIÇÕES", this.buildResultsTable(data.measurements || [], data)),
        this.buildSection("05", "CROQUI / ESQUEMA DE LOCALIZAÇÃO", this.buildCroqui(data.croqui)),
        this.buildSection("06", "OBSERVAÇÕES", this.buildObservations(data.observations)),
        this.buildSection("07", "TERMO DE RESPONSABILIDADE & SIGNATÁRIOS", this.buildSignatories(data.signatories || []))
      ],
      styles: {
        // Cabeçalho compartilhado (inspection-report-header.ts) — mesmos valores de sempre.
        ...INSPECTION_REPORT_HEADER_STYLES,
        footerText: { fontSize: 7.5, color: "#666666" },
        sectionTitle: { fontSize: 10, bold: true, color: "#FFFFFF" },
        tableHeader: { fontSize: 7.5, bold: true, color: "#002B49" },
        tableCell: { fontSize: 7.5, color: "#333333" },
        // Slightly smaller, for the results table alone, so seven columns
        // stay readable inside the page.
        resultsHeader: { fontSize: 6.8, bold: true, color: "#002B49" },
        resultsCell: { fontSize: 7, color: "#333333" },
        tableLabel: { fontSize: 7.5, bold: true, color: "#555555" },
        infoText: { fontSize: 7.5, color: "#666666", italics: true },
        termText: { fontSize: 7.5, color: "#333333" }
      },
      defaultStyle: { fontSize: 8 }
    };
  }

  /**
   * Header as approved in the previous revision — logo on the left, document
   * identification on the right, UNIDADE/ÁREA and TAG on the line below —
   * with the QR code between the two columns. Built by the shared
   * inspection-report-header.ts, which produces this exact definition.
   */
  private buildHeader(snapshot: DocumentSnapshot, data: any): Content {
    const reportNumber = snapshot.documentNumber || snapshot.id;

    return buildInspectionReportHeader({
      title: "RELATÓRIO DE MEDIÇÃO DE ESPESSURA\nPOR ULTRASSOM",
      qrReference: `INSPECT-IA:ME:${reportNumber}`,
      fields: [
        { label: "Rel. cliente nº", value: data.clientReportNumber || "-" },
        { label: "Rel. INSPECT IA nº", value: reportNumber },
        { label: "Revisão", value: snapshot.revision },
        { label: "Período", value: `${this.fmtDate(data.periodStart)} a ${this.fmtDate(data.periodEnd)}` },
        { label: "Data da emissão", value: this.fmtDate(data.issueDate) },
        { label: "Ordem de serviço", value: data.osNumber || data.osSubOs || "-" },
        { label: "Sub-OS", value: data.subOsNumber || "-" }
      ],
      unitArea: data.area || "-",
      tag: data.tag || "-",
      visual: data.visual ?? null
    });
  }

  private buildSection(number: string, title: string, content: Content): Content {
    return {
      stack: [
        {
          table: {
            widths: [18, "*"],
            body: [[
              { text: number, style: "sectionTitle", fillColor: "#002B49", alignment: "center", margin: [0, 3, 0, 3] },
              { text: title, style: "sectionTitle", fillColor: "#002B49", margin: [4, 3, 0, 3] }
            ]]
          },
          layout: "noBorders",
          margin: [0, 10, 0, 5]
        },
        content
      ]
    };
  }

  private buildIdentification(data: any): Content {
    return {
      table: {
        widths: ["16%", "17%", "16%", "17%", "16%", "18%"],
        body: [
          [
            { text: "CLIENTE", style: "tableLabel" }, { text: this.value(data.client), style: "tableCell" },
            { text: "PROJETO / CONTRATO", style: "tableLabel" }, { text: this.value(data.contract), style: "tableCell" },
            { text: "UNIDADE / ÁREA", style: "tableLabel" }, { text: this.value(data.area), style: "tableCell" }
          ],
          [
            { text: "EQUIPAMENTO / TAG", style: "tableLabel" }, { text: this.value(data.tag), style: "tableCell" },
            { text: "TIPO", style: "tableLabel" }, { text: this.value(data.equipmentType), style: "tableCell" },
            { text: "OS / SUB-OS", style: "tableLabel" }, { text: this.formatOsSubOs(data), style: "tableCell" }
          ],
          [
            { text: "DATA DO ENSAIO", style: "tableLabel" }, { text: this.fmtDate(data.date), style: "tableCell" },
            { text: "INSPETOR", style: "tableLabel" }, { text: this.value(data.inspector), style: "tableCell" },
            { text: "REVISÃO", style: "tableLabel" }, { text: this.value(data.revision || "00"), style: "tableCell" }
          ]
        ]
      },
      layout: CLEAN_LAYOUT
    };
  }

  private buildParameters(data: any): Content {
    const pairs: Array<[string, string]> = [
      ["PROCEDIMENTO / REVISÃO", data.procedure],
      ["NORMA DE REFERÊNCIA", data.norm],
      ["CRITÉRIO DE ACEITAÇÃO", data.acceptanceCriteria],
      ["FAMÍLIA DE MATERIAL", data.material],
      ["CONDIÇÃO SUPERFICIAL", data.surfaceCondition],
      ["TEMPERATURA DA MEDIÇÃO (TME)", data.temperature],
      ["Nº CERTIF. TERMÔMETRO", data.thermometerCert],
      ["ACOPLANTE", data.couplant],
      ["BLOCO DE CALIBRAÇÃO", data.calibrationBlock],
      ["Nº CERT. CALIBRAÇÃO BLOCO", data.calibrationCert],
      ["TÉCNICA DE MEDIÇÃO", data.technique],
      // Em alta temperatura o FC é POR PONTO (coluna FC da tabela) e o método é
      // fixo; fora dela, só um FC digitado de relatório antigo aparece.
      data.correctionMethod ? ["MÉTODO DE CORREÇÃO", data.correctionMethod] : ["FATOR DE CORREÇÃO", data.correctionFactor],
      ...(data.materialSpecification ? [["MATERIAL (ESPECIFICAÇÃO)", data.materialSpecification] as [string, string]] : [])
    ];

    const body: TableCell[][] = [];
    for (let i = 0; i < pairs.length; i += 2) {
      const left = pairs[i]!;
      const right = pairs[i + 1];
      body.push([
        { text: left[0], style: "tableLabel" }, { text: this.value(left[1]), style: "tableCell" },
        { text: right ? right[0] : "", style: "tableLabel" }, { text: right ? this.value(right[1]) : "", style: "tableCell" }
      ]);
    }

    return {
      table: { widths: ["25%", "25%", "25%", "25%"], body },
      layout: CLEAN_LAYOUT
    };
  }

  private buildEquipment(data: any): Content {
    const device: Array<[string, string]> = [
      ["FABRICANTE", data.deviceMaker],
      ["MODELO", data.deviceModel],
      ["Nº DE SÉRIE", data.deviceSerial],
      ["CERTIF. CALIBRAÇÃO", data.deviceCert],
      ["VALIDADE", this.fmtDate(data.deviceValidity)]
    ];
    const transducer: Array<[string, string]> = [
      ["FABRICANTE", data.transducerMaker],
      ["MODELO", data.transducerModel],
      ["TIPO", data.transducerType],
      ["FREQUÊNCIA", data.transducerFrequency],
      ["DIMENSÃO", data.transducerDimension],
      ["IDENTIFICAÇÃO", data.transducerId]
    ];

    const rows = Math.max(device.length, transducer.length);
    const body: TableCell[][] = [[
      { text: "APARELHO DE ME-US", style: "tableHeader", colSpan: 2, fillColor: "#F2F5F8" }, {},
      { text: "TRANSDUTOR", style: "tableHeader", colSpan: 2, fillColor: "#F2F5F8" }, {}
    ]];

    for (let i = 0; i < rows; i++) {
      const d = device[i];
      const t = transducer[i];
      body.push([
        { text: d ? d[0] : "", style: "tableLabel" }, { text: d ? this.value(d[1]) : "", style: "tableCell" },
        { text: t ? t[0] : "", style: "tableLabel" }, { text: t ? this.value(t[1]) : "", style: "tableCell" }
      ]);
    }

    return {
      table: { widths: ["22%", "28%", "22%", "28%"], body },
      layout: CLEAN_LAYOUT
    };
  }

  /**
   * Em MEDIÇÃO EM ALTA TEMPERATURA a tabela ganha TME, Emq, FC_A e Ea
   * (MÉTODO A — RESULTADO APROXIMADO), e a coluna medida passa a chamar-se
   * Emq. Não há coluna de calibração: o Método A não usa calibração.
   *
   * Todos os valores chegam prontos do modelo documental — o PDF NÃO recalcula
   * a correção. Recalcular aqui faria a reimpressão de um relatório aprovado
   * mudar quando o Procedimento vinculado fosse alterado, que é exatamente o
   * que o retrato gravado por ponto e o congelamento do snapshot impedem.
   * PREVIEW = PDF: os dois leem o mesmo modelo.
   */
  private buildResultsTable(measurements: any[], data: any = {}): Content {
    const highTemperature = data.highTemperature === true;

    const header: TableCell[] = highTemperature
      ? [
          { text: "ID DO PONTO", style: "resultsHeader" },
          { text: "COMPONENTE", style: "resultsHeader" },
          { text: "LOCALIZAÇÃO", style: "resultsHeader" },
          { text: "ESP. NOM.\n(mm)", style: "resultsHeader", alignment: "center" },
          { text: "ESP. MÍN.\n(mm)", style: "resultsHeader", alignment: "center" },
          { text: "TME\n(°C)", style: "resultsHeader", alignment: "center" },
          { text: "Emq\n(mm)", style: "resultsHeader", alignment: "center" },
          { text: "FC_A", style: "resultsHeader", alignment: "center" },
          { text: "Ea\n(mm)", style: "resultsHeader", alignment: "center" },
          { text: "RESULTADO", style: "resultsHeader", alignment: "center" }
        ]
      : [
          { text: "ID DO PONTO", style: "resultsHeader" },
          { text: "COMPONENTE", style: "resultsHeader" },
          { text: "LOCALIZAÇÃO", style: "resultsHeader" },
          { text: "ESP. NOM.\n(mm)", style: "resultsHeader", alignment: "center" },
          { text: "ESP. MÍN.\n(mm)", style: "resultsHeader", alignment: "center" },
          { text: "ESP. ATUAL\n(mm)", style: "resultsHeader", alignment: "center" },
          { text: "LAUDO", style: "resultsHeader", alignment: "center" }
        ];

    const columns = header.length;
    const body: TableCell[][] = [header];

    if (!Array.isArray(measurements) || measurements.length === 0) {
      const filler: TableCell[] = [{ text: "—", style: "infoText", colSpan: columns, alignment: "center" }];
      for (let i = 1; i < columns; i++) filler.push({});
      body.push(filler);
    } else {
      // Values arrive already formatted (0,00) with the laudo derived from
      // the numbers, so the document never disagrees with the screen.
      for (const m of measurements) {
        const row: TableCell[] = [
          { text: this.value(m.id), style: "resultsCell" },
          { text: this.value(m.component), style: "resultsCell" },
          { text: this.value(m.location), style: "resultsCell" },
          { text: this.value(m.nominalThickness), style: "resultsCell", alignment: "center" },
          { text: this.value(m.minThickness), style: "resultsCell", alignment: "center" }
        ];
        if (highTemperature) {
          row.push({ text: this.value(m.tme), style: "resultsCell", alignment: "center" });
          row.push({ text: this.value(m.emq), style: "resultsCell", alignment: "center" });
          row.push({ text: m.hotStatus === "CORRECTED" ? this.value(m.fcA) : this.hotStatusLabel(m.hotStatus), style: "resultsCell", alignment: "center" });
          row.push({ text: m.hotStatus === "CORRECTED" ? this.value(m.ea) : "—", style: "resultsCell", alignment: "center" });
        } else {
          row.push({ text: this.value(m.actualThickness), style: "resultsCell", alignment: "center" });
        }
        row.push({ text: this.value(m.laudo), style: "resultsCell", alignment: "center", bold: m.laudo === "R" || m.laudo === "ADPC" });
        body.push(row);
      }
    }

    // Content widths + cell padding add up to exactly the usable width, so no
    // column can be pushed past the right margin.
    const table: Content = {
      table: {
        headerRows: 1,
        widths: highTemperature ? RESULTS_WIDTHS_HT : RESULTS_WIDTHS,
        body,
        dontBreakRows: true
      },
      layout: RESULTS_LAYOUT
    };

    if (!highTemperature) return table;

    // A legenda das abreviações acompanha a tabela: quem lê o documento
    // impresso não tem onde consultar o que significa Emq ou Ea.
    const legendLines: string[] = Array.isArray(data.highTemperatureLegend) ? data.highTemperatureLegend : [];
    const correction = data.thermalCorrection;
    const method = [data.correctionMethod || "MÉTODO A — RESULTADO APROXIMADO", correction?.procedureCode].filter(Boolean).join(" · ");
    const notice = !correction || correction.status === "NOT_APPLICABLE"
      ? ""
      : correction.status === "CORRECTED"
        ? `Correção aproximada aplicada — ${method}. Ea = Emq × FC_A; FC_A = (Vsa − k×ΔT) ÷ Vsa.`
        // Relatório antigo, de um motor removido (motor de coeficientes ou Método B): diz o que ele dizia.
        : correction.status === "RULE_NOT_CONFIGURED"
          ? `Correção não aplicada (registro anterior ao Método A) — ${correction.message || ""}`
          : `Correção não aplicada em ${correction.pendingPoints ?? "—"} ponto(s) — ${method}. ${correction.message || "Faltam dados de origem controlada (Vsa/k/Temperatura de Referência do Procedimento) para esta medição."}`;

    return {
      stack: [
        table,
        ...(legendLines.length > 0 ? [{ text: legendLines.join("  ·  "), style: "infoText", margin: [0, 5, 0, 0] as [number, number, number, number] }] : []),
        ...(notice ? [{ text: notice, style: "infoText", bold: true, margin: [0, 3, 0, 0] as [number, number, number, number] }] : [])
      ]
    };
  }

  /** Célula FC_A quando o ponto não foi corrigido: o motivo, nunca vazio. */
  private hotStatusLabel(status: unknown): string {
    switch (status) {
      case "INCOMPLETE": return "INCOMPLETO";
      default: return "—";
    }
  }

  private buildCroqui(croquiBase64?: string | null): Content {
    // "data:image" não basta: o pdfmake só desenha PNG e JPEG. Um GIF/WEBP,
    // um base64 truncado ou um marcador de imagem removida passariam por essa
    // checagem e derrubariam o documento inteiro com "Unknown image format".
    if (!isPdfDrawableImage(croquiBase64)) {
      return {
        table: {
          widths: ["*"],
          body: [[{ text: "\nÁrea destinada ao croqui, esquema ou identificação gráfica dos pontos de medição.\n", style: "infoText", alignment: "center" }]]
        },
        layout: BOX_LAYOUT
      };
    }
    return { image: croquiBase64, fit: [USABLE_WIDTH - 20, 260], alignment: "center", margin: [0, 4, 0, 0] };
  }

  /**
   * Observações absorbs the legend/acceptance criteria text — there is no
   * separate "Legenda e Critérios" section anymore.
   */
  private buildObservations(comments?: string | null): Content {
    const text = typeof comments === "string" ? comments.trim() : "";

    return {
      table: {
        widths: ["*"],
        body: [
          [{ text: text || "—", style: "tableCell", margin: [0, 2, 0, 12] }],
          [{
            stack: [
              {
                text: "(A) APROVADO   |   (R) REPROVADO   |   (EC) EXAME COMPLEMENTAR   |   (P) EM ELABORAÇÃO   |   (ADPC) A SER DEFINIDO PELO CLIENTE",
                style: "tableCell",
                bold: true
              },
              {
                text: "O enquadramento final deverá refletir o critério técnico aplicável e os dados efetivamente medidos.",
                style: "infoText",
                margin: [0, 3, 0, 0]
              }
            ]
          }]
        ]
      },
      layout: BOX_LAYOUT
    };
  }

  private buildSignatories(signatories: any[]): Content {
    const list = Array.isArray(signatories) && signatories.length > 0
      ? signatories
      : [
          { name: "", role: "INSPETOR", cred: "" },
          { name: "", role: "ENGENHEIRO PLH", cred: "" },
          { name: "", role: "FISCAL", cred: "" }
        ];

    const body: TableCell[][] = [
      [
        { text: "NOME", style: "tableHeader", fillColor: "#F2F5F8" },
        { text: "FUNÇÃO", style: "tableHeader", fillColor: "#F2F5F8" },
        { text: "CREDENCIAL", style: "tableHeader", fillColor: "#F2F5F8" },
        { text: "ASSINATURA", style: "tableHeader", fillColor: "#F2F5F8", alignment: "center" }
      ]
    ];

    for (const s of list) {
      body.push([
        { text: this.value(s?.name), style: "tableCell", margin: [0, 10, 0, 10] },
        { text: this.value(s?.role), style: "tableCell", margin: [0, 10, 0, 10] },
        { text: this.value(s?.cred), style: "tableCell", margin: [0, 10, 0, 10] },
        this.buildSignatureCell(s)
      ]);
    }

    return {
      stack: [
        {
          text: "Responsabilizam-se pelas informações aqui contidas, para os devidos efeitos, os signatários abaixo. " +
                "Declaram que o ensaio foi executado conforme o procedimento e a norma indicados neste relatório, que os " +
                "equipamentos utilizados encontravam-se com calibração vigente no momento da execução, e que os resultados " +
                "registrados refletem fielmente as medições realizadas.",
          style: "termText",
          margin: [0, 0, 0, 8]
        },
        {
          table: { widths: ["28%", "22%", "22%", "28%"], body },
          layout: CLEAN_LAYOUT
        }
      ]
    };
  }

  /** Prints the registered signature image; falls back to a signing line when there is none. */
  private buildSignatureCell(signatory: any): TableCell {
    const image = signatory?.signatureImage;
    if (isDrawableByPdfMake(image)) {
      return { image, fit: [110, 34], alignment: "center", margin: [0, 3, 0, 3] } as TableCell;
    }
    return { text: "\n__________________________\n", style: "tableCell", alignment: "center" };
  }

  /** "—" for anything empty, so a missing optional field never leaves a blank cell or breaks the layout. */
  private value(input: unknown): string {
    if (input === null || input === undefined) return "—";
    const text = String(input).trim();
    return text.length > 0 ? text : "—";
  }

  private formatOsSubOs(data: any): string {
    const os = data?.osNumber ? String(data.osNumber).trim() : "";
    const sub = data?.subOsNumber ? String(data.subOsNumber).trim() : "";
    const combined = [os, sub].filter(Boolean).join(" / ");
    return combined || this.value(data?.osSubOs);
  }

  /**
   * Formats a date as a civil date. "2026-09-06" is rendered as 06/09/2026
   * in any timezone — parsing it through Date() would shift it a day back
   * west of Greenwich, since the string is read as UTC midnight.
   */
  private fmtDate(value?: string | null): string {
    if (!value) return "—";
    const raw = String(value).trim();
    if (!raw) return "—";

    const isoDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (isoDate) {
      const [, year, month, day] = isoDate;
      return `${day}/${month}/${year}`;
    }

    const parsed = new Date(raw);
    if (isNaN(parsed.getTime())) return raw;
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}/${parsed.getFullYear()}`;
  }
}

// Register builder immediately
documentEngine.registerBuilder("ME", new MeBuilder());
